import { Pressable, Text, View } from "react-native";
import Checkbox from "expo-checkbox";
import { zodResolver } from "@hookform/resolvers/zod";
import { decodeEntity } from "html-entities";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { z } from "zod";

import { useHonoClient } from "~/hooks/HonoContext";
import { Button } from "~/ui/Button";
import * as SD from "~/ui/SheetDropdown";
import { TextInput } from "~/ui/TextInput";

const MOOD_EMOJIS = [
  { emoji: "😔", label: "Sad" },
  { emoji: "😐", label: "Neutral" },
  { emoji: "😊", label: "Happy" },
] as const;

const feedbackSchema = z.object({
  feedback: z.string().min(1, "Please enter your feedback"),
  mood: z.string().optional(),
  canContact: z.boolean().optional(),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

export function FeedbackForm() {
  return (
    <SD.Root modal={false}>
      <SD.Trigger asChild>
        <Button
          variant="outline"
          size="small"
          className="hover:bg-surface-hover"
        >
          Feedback
        </Button>
      </SD.Trigger>
      <SD.Content
        className="flex min-w-80 flex-col gap-3"
        align="end"
        side="bottom"
        sideOffset={16}
      >
        <SDContent />
      </SD.Content>
    </SD.Root>
  );
}

function SDContent() {
  const { hc } = useHonoClient();
  const { mutateAsync, isSuccess } = useMutation({
    mutationFn: (data: FeedbackFormData) => {
      return hc.feedback.$post({
        json: {
          feedback: data,
        },
      });
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      feedback: "",
      mood: "",
      canContact: false,
    },
  });

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      await mutateAsync(data);
      reset();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  return (
    <View className="gap-4 p-4">
      {isSuccess ? (
        <View className="items-center gap-4 py-8">
          <Text className="text-text-primary text-lg">Thank you! 🎉</Text>
          <Text className="text-text-secondary text-center">
            We appreciate your feedback and will use it to improve your
            experience.
          </Text>
        </View>
      ) : (
        <>
          <Text className="text-text-primary text-lg font-medium">
            Share your feedback
          </Text>
          <Text className="text-text-secondary">
            We would love to hear your thoughts to improve your experience!
          </Text>

          <Controller
            control={control}
            name="feedback"
            render={({ field: { onChange, value } }) => (
              <View>
                <Text className="text-text-primary mb-2">
                  What{decodeEntity("&apos;")}s on your mind?
                </Text>

                <TextInput
                  autoFocus
                  multiline
                  value={value}
                  onChangeText={onChange}
                  placeholder="The product is great, but..."
                  className="bg-surface-tertiary min-h-24 p-2"
                  errorMessage={errors.feedback?.message}
                />
              </View>
            )}
          />

          <View>
            <Text className="text-text-primary mb-2">How are you feeling?</Text>
            <View className="flex-row gap-4">
              {MOOD_EMOJIS.map(({ emoji }) => (
                <Controller
                  key={emoji}
                  control={control}
                  name="mood"
                  render={({ field: { onChange, value } }) => (
                    <Button
                      variant={value === emoji ? "primary" : "secondary"}
                      onPress={() => onChange(emoji)}
                      className="flex-1"
                    >
                      <Text className="text-xl">{emoji}</Text>
                    </Button>
                  )}
                />
              ))}
            </View>
          </View>

          <Controller
            control={control}
            name="canContact"
            render={({ field: { onChange, value } }) => (
              <Pressable
                className="flex-row items-center gap-2"
                onPress={() => onChange(!value)}
              >
                <Checkbox
                  value={value}
                  onValueChange={(checked: boolean) => onChange(checked)}
                  id="contact-consent"
                />
                <Text className="text-text-primary">
                  Can we contact you about your feedback?
                </Text>
              </Pressable>
            )}
          />

          <Button
            variant="primary"
            onPress={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
            Submit feedback
          </Button>
        </>
      )}
    </View>
  );
}
